import * as SplashScreen from "expo-splash-screen";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Appearance, Platform, View } from "react-native";
import { createStore, useStore } from "zustand";

import { clientStorage } from "@/lib/platform/clientStorage";
import { useColorScheme } from "@/theme/colorScheme";
import { palette, type ThemeName } from "@/theme/palette";

const STORAGE_KEY = "trovie.theme";

/**
 * The persisted theme override, or `null` when the visitor has never toggled, which means follow
 * the OS (ADR-0002). Module-private: the app keeps both schemes and forces neither, so
 * `ThemeProvider` is the only reader. A failed read returns `null`, the same fallback the provider
 * takes.
 */
async function readStoredTheme(): Promise<ThemeName | null> {
  try {
    const stored = await clientStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    return null;
  }
}

// Keep the native splash screen from auto-hiding at bundle load, so the first frame the user sees
// is already in the persisted theme, with no light/dark flash on cold start (#133). ThemeProvider
// reveals the UI once the stored theme resolves. The catch is required: preventAutoHideAsync
// rejects if the splash is already gone (for example on a fast-refresh remount), and that must not
// crash module load.
void SplashScreen.preventAutoHideAsync().catch(() => {});

/**
 * Applies the persisted theme override on launch. With no stored choice the app follows the OS;
 * applying that value explicitly adds NativeWind's web `dark` class, which keeps CSS tokens aligned
 * with `useThemeColors()` on public routes. An explicit toggle persists and wins afterwards
 * (ADR-0002).
 *
 * The native splash stays up until the stored theme resolves, and is hidden after the themed frame
 * commits, so cold start never flashes the wrong theme (#133). The resolved choice applies on the
 * next event-loop turn, after NativeWind subscribers mount. `readStoredTheme` never rejects, so the
 * splash always hides.
 *
 * The read runs once per mount. `useColorScheme()` returns a fresh object every render, so
 * depending on it directly would re-read storage every render and, because `useThemeToggle`
 * persists without awaiting, could re-apply a pre-toggle value over the visitor's choice. The
 * latest hook result is reached through a ref instead; destructuring `setColorScheme` from it would
 * unbind the method from its receiver.
 */
export function ThemeProvider({ children }: { children: ReactNode; }) {
  const colorScheme = useColorScheme();
  const choice = useStore(appearanceStore, state => state.choice);
  const scheme = useRef(colorScheme);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    scheme.current = colorScheme;
  }, [colorScheme]);
  useEffect(() => {
    let cancelled = false;
    let applyTimer: ReturnType<typeof setTimeout> | undefined;
    void readStoredTheme()
      .then((stored) => {
        if (cancelled) return;
        // NativeWind's subscribers need one committed frame before its imperative setter runs.
        // Deferring avoids React's "state update ... hasn't mounted yet" warning on web cold loads.
        applyTimer = setTimeout(() => {
          if (cancelled) return;
          seedChoice(stored);
          applyAppearance(scheme.current, stored ?? "system");
          setReady(true);
        }, 0);
      });
    return () => {
      cancelled = true;
      if (applyTimer !== undefined) clearTimeout(applyTimer);
    };
  }, []);
  useEffect(() => {
    if (!ready || choice !== "system" || Platform.OS !== "web") return undefined;
    // NativeWind's class-based web mode needs an explicit CSS class on each OS change.
    const sync = () => applyAppearance(scheme.current, "system");
    const subscription = Appearance.addChangeListener(sync);
    sync();
    return () => subscription.remove();
  }, [ready, choice]);
  // Runs after the render that applied the resolved theme commits, so hiding the splash reveals a
  // correctly themed frame with no flash or blank gap.
  useEffect(() => {
    if (ready) void SplashScreen.hideAsync().catch(() => {});
  }, [ready]);
  return <View className="flex-1">{children}</View>;
}

/**
 * The active palette (light or dark) for imperative styles: the chart fills, icon tints and native
 * chrome that cannot read a CSS variable. The colour scheme is the only input, because one design
 * language runs on every platform.
 */
export function useThemeColors() {
  const { colorScheme } = useColorScheme();
  const name: ThemeName = colorScheme === "dark" ? "dark" : "light";
  return palette[name];
}

/**
 * What the vendor has chosen, which is a different question from which palette is showing.
 *
 * `useColorScheme()` answers "light or dark right now" and cannot tell "dark because they picked
 * dark" from "dark because the phone is". Appearance is a three-way setting on every platform that
 * has one (iOS calls the third option Automatic, Android calls it System), and a two-way toggle has
 * no way back to the third once tapped.
 *
 * A missing stored key means "system", which follows subsequent OS appearance changes. So
 * the choice is derived from whether the key exists, not from the palette in effect.
 */
export type AppearanceChoice = "system" | "light" | "dark";

function applyAppearance(scheme: ReturnType<typeof useColorScheme>, choice: AppearanceChoice) {
  scheme.setColorScheme(
    choice === "system" && Platform.OS === "web"
      ? Appearance.getColorScheme() === "dark" ? "dark" : "light"
      : choice,
  );
}

const appearanceStore = createStore<{ choice: AppearanceChoice; }>()(() => ({
  choice: "system",
}));

/** Seeded once by `ThemeProvider` from the same read that resolves the palette. */
function seedChoice(stored: ThemeName | null) {
  appearanceStore.setState({ choice: stored ?? "system" });
}

/**
 * Read and set the appearance choice.
 *
 * Choosing `system` removes the stored key rather than writing the current OS value, so a phone
 * that later switches on its own schedule (sunset, Focus, Low Power) is followed instead of frozen
 * at the value it had when the vendor chose to follow it.
 */
export function useAppearanceChoice(): {
  choice: AppearanceChoice;
  setChoice: (next: AppearanceChoice) => void;
} {
  // Held as the object, never destructured: `setColorScheme` is a method, and pulling it off the
  // fresh object nativewind returns each render unbinds it from its receiver.
  const scheme = useColorScheme();
  const choice = useStore(appearanceStore, (state) => state.choice);
  return {
    choice,
    setChoice: (next) => {
      appearanceStore.setState({ choice: next });
      if (next === "system") {
        void clientStorage.removeItem(STORAGE_KEY);
        applyAppearance(scheme, "system");
        return;
      }
      scheme.setColorScheme(next);
      void clientStorage.setItem(STORAGE_KEY, next);
    },
  };
}

/** Flip between light and dark and persist the choice. */
export function useThemeToggle() {
  const colorScheme = useColorScheme();
  return () => {
    const next: ThemeName = colorScheme.colorScheme === "dark" ? "light" : "dark";
    colorScheme.setColorScheme(next);
    appearanceStore.setState({ choice: next });
    void clientStorage.setItem(STORAGE_KEY, next);
  };
}
